import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, auth, storage } from '../firebase';
import { collection, onSnapshot, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const HARVEST_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/harvestClientPhotos';
const TAG_UPLOADS_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/tagUploadedPhotos';

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
// Claude vision rejects images over 8000px on a side, and blogs push the stored
// file to WordPress as the featured image, so pro-camera/drone originals are
// scaled down before upload. 2560 matches WordPress's own big-image threshold.
const MAX_UPLOAD_EDGE = 2560;

// Returns the file unchanged when it's already within MAX_UPLOAD_EDGE (or the
// browser can't decode it), otherwise a JPEG re-encode scaled to fit.
async function downscaleForUpload(file) {
  let bitmap;
  try { bitmap = await createImageBitmap(file); } catch { return file; }
  const longEdge = Math.max(bitmap.width, bitmap.height);
  if (longEdge <= MAX_UPLOAD_EDGE) { bitmap.close(); return file; }
  const scale = MAX_UPLOAD_EDGE / longEdge;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  return blob || file;
}

function fmtDate(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

const thumbStyle = {
  width: '100%',
  height: 160,
  objectFit: 'cover',
  borderRadius: '8px 8px 0 0',
  display: 'block',
  background: 'var(--bg-secondary)',
};

function PhotoCard({ photo, busy, actions, footer }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', opacity: busy ? 0.6 : 1 }}>
      <a href={photo.sourceUrl} target="_blank" rel="noreferrer" title="Open full size">
        {broken ? (
          <div style={{ ...thumbStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            image unavailable
          </div>
        ) : (
          <img src={photo.sourceUrl} alt={photo.description || ''} loading="lazy" style={thumbStyle} onError={() => setBroken(true)} />
        )}
      </a>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {(photo.stockSuspect || photo.foundOnSitePage || photo.harvestMethod === 'upload') && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {photo.stockSuspect && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,.12)', border: '1px solid rgba(251,191,36,.35)', borderRadius: 20, padding: '2px 10px' }}>
                ⚠ Possible stock photo
              </span>
            )}
            {photo.foundOnSitePage && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#4ade80', background: 'rgba(22,163,74,.10)', border: '1px solid rgba(22,163,74,.3)', borderRadius: 20, padding: '2px 10px' }}>
                ✓ On a site page
              </span>
            )}
            {photo.harvestMethod === 'upload' && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#38bdf8', background: 'rgba(56,189,248,.10)', border: '1px solid rgba(56,189,248,.3)', borderRadius: 20, padding: '2px 10px' }}>
                ⇧ Uploaded
              </span>
            )}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5 }}>
          {photo.description || photo.title || <span style={{ color: 'var(--text-muted)' }}>No description</span>}
        </div>
        {(photo.tags || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {photo.tags.map(t => (
              <span key={t} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: 20, padding: '2px 9px' }}>{t}</span>
            ))}
          </div>
        )}
        {footer}
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>{actions}</div>
      </div>
    </div>
  );
}

export default function PhotoLibrary() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [photos, setPhotos] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [showRejected, setShowRejected] = useState(false);
  const [guideText, setGuideText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  useEffect(() => {
    setScanResult(null);
    setError('');
    setShowRejected(false);
    setUploadNote('');
    setGuideText(clients.find(c => c.id === selectedClientId)?.photoScanUrls || '');
    if (!selectedClientId) { setPhotos([]); return; }
    const unsub = onSnapshot(
      query(collection(db, 'clientPhotos'), where('clientId', '==', selectedClientId)),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setPhotos(all);
      },
      err => console.error('clientPhotos snapshot error:', err)
    );
    return unsub;
  }, [selectedClientId]);

  const client = clients.find(c => c.id === selectedClientId) || null;
  const pending = useMemo(() => photos.filter(p => p.status === 'pending'), [photos]);
  const approved = useMemo(() => photos.filter(p => p.status === 'approved'), [photos]);
  const rejected = useMemo(() => photos.filter(p => p.status === 'rejected'), [photos]);

  const handleScan = async () => {
    setError('');
    setScanResult(null);
    setScanning(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to scan.');
      const guideUrls = guideText.split('\n').map(s => s.trim()).filter(s => /^https?:\/\//i.test(s));
      const badLines = guideText.split('\n').map(s => s.trim()).filter(s => s && !/^https?:\/\//i.test(s));
      if (badLines.length) throw new Error(`These guide lines aren't full URLs (must start with https://): ${badLines.join(', ')}`);
      // Remember the guidance on the client record so the next scan pre-fills it
      updateDoc(doc(db, 'clients', selectedClientId), { photoScanUrls: guideText.trim() }).catch(() => {});
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(HARVEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId: selectedClientId, guideUrls }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Scan failed');
      setScanResult(data);
    } catch (err) {
      setError(err.message);
    }
    setScanning(false);
  };

  const setStatus = async (photoId, status) => {
    setBusyId(photoId);
    try {
      await updateDoc(doc(db, 'clientPhotos', photoId), { status, reviewedAt: new Date() });
    } catch (err) {
      setError(`Could not update photo: ${err.message}`);
    }
    setBusyId(null);
  };

  const handleUpload = async (e) => {
    const files = [...(e.target.files || [])];
    if (fileRef.current) fileRef.current.value = '';
    if (!files.length || !selectedClientId) return;
    setError('');
    setUploadNote('');

    const bad = files.filter(f => !UPLOAD_TYPES.includes(f.type) || f.size > MAX_UPLOAD_BYTES);
    if (bad.length) {
      setError(`Skipping ${bad.map(f => f.name).join(', ')} — photos must be JPG, PNG, or WEBP under 15 MB.`);
    }
    const good = files.filter(f => !bad.includes(f));
    if (!good.length) return;

    setUploading(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to upload.');
      const uploads = [];
      for (let i = 0; i < good.length; i++) {
        const f = good[i];
        setUploadNote(`Uploading ${i + 1} of ${good.length}: ${f.name}…`);
        const body = await downscaleForUpload(f);
        let safeName = f.name.replace(/[^\w.-]+/g, '_').slice(-80);
        if (body !== f) safeName = safeName.replace(/\.(png|webp|jpe?g)$/i, '') + '.jpg';
        const storageRef = ref(storage, `clientPhotos/${selectedClientId}/${Date.now()}-${safeName}`);
        await uploadBytes(storageRef, body, { contentType: body.type || f.type });
        uploads.push({ url: await getDownloadURL(storageRef), name: f.name });
      }

      setUploadNote(`Reviewing and tagging ${uploads.length} photo${uploads.length === 1 ? '' : 's'}…`);
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(TAG_UPLOADS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId: selectedClientId, uploads }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Tagging failed');
      setUploadNote(
        `${data.saved} photo${data.saved === 1 ? '' : 's'} added to the approved library`
        + (data.alreadyKnown ? `, ${data.alreadyKnown} already in the library` : '')
        + (data.tagFailed ? `, ${data.tagFailed} saved without auto-tags` : '') + '.'
      );
    } catch (err) {
      setError(err.message);
      setUploadNote('');
    }
    setUploading(false);
  };

  const bulkSetStatus = async (targets, status) => {
    for (const p of targets) {
      try { await updateDoc(doc(db, 'clientPhotos', p.id), { status, reviewedAt: new Date() }); }
      catch (err) { setError(`Could not update photo: ${err.message}`); break; }
    }
  };
  const approveAllUnflagged = () => bulkSetStatus(pending.filter(p => !p.stockSuspect), 'approved');
  const rejectAllFlagged = () => bulkSetStatus(pending.filter(p => p.stockSuspect), 'rejected');

  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14 };

  return (
    <div className="page" style={{ maxWidth: 1200 }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Photo Library</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Real project photos harvested from each client's website. Approved photos are automatically used as blog featured images when they match the article topic; everything else falls back to AI images.
        </p>
      </div>

      {/* Client picker + scan */}
      <div className="card" style={{ padding: 22, marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
          <div className="form-field" style={{ marginBottom: 0, minWidth: 260 }}>
            <label>Client</label>
            <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} disabled={scanning} style={{ width: '100%' }}>
              <option value="">— Choose a client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
            </select>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleScan}
            disabled={!selectedClientId || scanning || uploading}
          >
            {scanning ? 'Scanning site… this can take a few minutes' : 'Scan Site for Photos'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <button
            className="btn btn-secondary"
            onClick={() => fileRef.current?.click()}
            disabled={!selectedClientId || scanning || uploading}
            title="Add photos the client sent you that aren't on their website — they go straight to the approved library"
          >
            {uploading ? 'Uploading…' : 'Upload Photos'}
          </button>
        </div>

        {uploadNote && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#4ade80', background: 'rgba(22,163,74,.08)', border: '1px solid rgba(22,163,74,.3)', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
            {uploadNote}
          </div>
        )}

        {client && (
          <div className="form-field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label>
              Guide the scan <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional. List pages where the real photos live (one URL per line); the scan covers those pages and their subpages only. Leave blank to scan the whole site.</span>
            </label>
            <textarea
              rows={2}
              placeholder={`e.g. ${(client.clientUrl || 'https://client.com').replace(/\/$/, '')}/projects/`}
              value={guideText}
              onChange={e => setGuideText(e.target.value)}
              disabled={scanning}
              style={{ fontSize: 12, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        )}

        {client && !scanning && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
            {guideText.trim()
              ? 'Guided scan: only the pages listed above (plus their subpages) will be checked for photos. The guidance is saved per client.'
              : <>Scans {client.clientUrl || 'the client site'} for real project photos. New finds land in Pending Review below — nothing is used in blogs until you approve it. Rescanning skips photos you've already reviewed.</>}
          </p>
        )}

        {error && <div className="error-box" style={{ marginTop: 12, fontSize: 12 }}>{error}</div>}
        {scanResult && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#4ade80', background: 'rgba(22,163,74,.08)', border: '1px solid rgba(22,163,74,.3)', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
            Scan complete ({scanResult.method === 'guided' ? 'guided pages' : scanResult.method === 'wp-media-api' ? 'WordPress media library' : 'page scrape'}): {scanResult.scanned} images found, {scanResult.saved} new photo{scanResult.saved === 1 ? '' : 's'} added for review
            {scanResult.method === 'wp-media-api' && scanResult.onSitePage > 0 && <> ({scanResult.onSitePage} from service/gallery pages, tagged first)</>}
            {scanResult.skippedNonPhoto > 0 && <>, {scanResult.skippedNonPhoto} skipped (logos/graphics/headshots)</>}
            {scanResult.alreadyKnown > 0 && <>, {scanResult.alreadyKnown} already reviewed</>}
            {scanResult.tagFailed > 0 && <>, {scanResult.tagFailed} unreadable</>}.
            {scanResult.capped && <> Hit the per-scan limit — run the scan again to continue where it left off.</>}
          </div>
        )}
      </div>

      {selectedClientId && (
        <>
          {/* Pending review */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Pending Review {pending.length > 0 && <span style={{ color: '#fbbf24' }}>({pending.length})</span>}
            </h2>
            {pending.some(p => !p.stockSuspect) && (
              <button className="btn btn-sm btn-secondary" onClick={approveAllUnflagged}>
                Approve all unflagged ({pending.filter(p => !p.stockSuspect).length})
              </button>
            )}
            {pending.some(p => p.stockSuspect) && (
              <button className="btn btn-sm btn-secondary" onClick={rejectAllFlagged}>
                Reject all flagged ({pending.filter(p => p.stockSuspect).length})
              </button>
            )}
          </div>
          {pending.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
              Nothing waiting for review{photos.length === 0 ? ' — run a scan to get started' : ''}.
            </p>
          ) : (
            <div style={{ ...grid, marginBottom: 28 }}>
              {pending.map(p => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  busy={busyId === p.id}
                  actions={
                    <>
                      <button className="btn btn-sm btn-primary" disabled={busyId === p.id} onClick={() => setStatus(p.id, 'approved')}>Approve</button>
                      <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => setStatus(p.id, 'rejected')}>Reject</button>
                    </>
                  }
                />
              ))}
            </div>
          )}

          {/* Approved library */}
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
            Approved Library {approved.length > 0 && <span style={{ color: '#4ade80' }}>({approved.length})</span>}
          </h2>
          {approved.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>No approved photos yet — blogs will keep using AI images until photos are approved here.</p>
          ) : (
            <div style={{ ...grid, marginBottom: 28 }}>
              {approved.map(p => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  busy={busyId === p.id}
                  footer={
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {p.usedCount ? `Used in ${p.usedCount} post${p.usedCount === 1 ? '' : 's'}` : 'Not used yet'}
                      {p.lastUsedTitle && <> · last: “{p.lastUsedTitle}”</>}
                    </div>
                  }
                  actions={
                    <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => setStatus(p.id, 'rejected')}>Remove from library</button>
                  }
                />
              ))}
            </div>
          )}

          {/* Rejected (collapsed) */}
          {rejected.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowRejected(s => !s)}>
                {showRejected ? 'Hide' : 'Show'} rejected ({rejected.length})
              </button>
              {showRejected && (
                <div style={{ ...grid, marginTop: 14 }}>
                  {rejected.map(p => (
                    <PhotoCard
                      key={p.id}
                      photo={p}
                      busy={busyId === p.id}
                      footer={<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Rejected {fmtDate(p.reviewedAt)}</div>}
                      actions={
                        <button className="btn btn-sm btn-secondary" disabled={busyId === p.id} onClick={() => setStatus(p.id, 'pending')}>Restore to pending</button>
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
